import { useAccess } from '@sitemax/smx-permission';
import { ArrowRightOutlined } from '@ant-design/icons';

import { useModal } from 'hooks/useModal';
import { useAppDispatch } from 'utilities/hooks';
import { useAppSelector } from 'utilities/hooks';
import { setParams } from 'slices/categoriesSlice';
import { moveDocument, refreshDocumentTreeLevels } from 'slices/documentsActions';
import { moveCategory } from 'slices/categoriesActions';
import { containingFoldersOf } from 'utilities/documentTree';
import { Category, Document } from 'interfaces/documents';

export const Move = (
    rows: (Category | Document)[],
    fetchData: () => void
) => {
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const { breadcrumbs } = useAppSelector((state) => state.documents);
    const isProjectDocument = breadcrumbs?.length > 0 ? breadcrumbs[breadcrumbs.length - 1].is_project_document : false;
    const canManage = useAccess(isProjectDocument ? ['files.project.manage'] : ['files.all.manage']);

    const moveItem = async (destination: Category | null) => {
        const sources = containingFoldersOf(rows);

        const promises = rows.map(row => {
            if ((row as Category).type === 'category') {
                return dispatch(moveCategory({
                    id: (row as Category).uuid,
                    parent_id: destination?.id || null,
                }));
            } else {
                return dispatch(moveDocument({
                    id: (row as Document).id,
                    category_id: destination?.id || null,
                }));
            }
        });

        await Promise.all(promises);

        dispatch(refreshDocumentTreeLevels([...sources, destination?.id]));

        fetchData();
    };

    if (!canManage) return null;

    return {
        key: 'Move',
        label: 'Move',
        onClick: () => {
            dispatch(setParams({
                excluded_ids: rows.map(row => {
                    if ((row as Category).type === 'category') {
                        return (row as Category).uuid;
                    } else {
                        return (row as Document).id;
                    }
                }),
            }));
            openModal('categoriesTreeModal', {
                title: 'Move to...',
                submitLabel: 'Move',
                cancelLabel: 'Cancel',
                parentId: (rows[0] as Category).parent_id || (rows[0] as Document).category_id,
            }, {
                onSubmit: (value: Category | null) => {
                    moveItem(value);
                },
            });
        },
        icon: <ArrowRightOutlined />,
        disabled: !rows.length,
    };
};
